import { getApiBaseUrl } from "@/config/apiUrl";

export type InvitationValidationMode = "ref" | "code";

// ============================================================================
// Pack Config Types (for MuxVideoPlayerWithCTA integration)
// ============================================================================

export interface PackPriceByCountry {
  amount: number;
  currency: string;
  playbackId?: string;
  ctaUrl?: string;
  ctaLabelEs?: string;
  ctaLabelEn?: string;
  isActive?: boolean;
  updatedAt?: string;
}

export interface PackConfig {
  id: string;
  name: string;
  nameEs: string;
  description: string;
  descriptionEs: string;
  amount: number;
  currency: string;
  thumbnail?: string;
  fallbackPlaybackId?: string;
  priceByCountry?: Record<string, PackPriceByCountry>;
}

// ============================================================================
// Invitation Validation Types
// ============================================================================

export interface InvitationValidateApiResponse {
  valid: boolean;
  message?: string;
  error?: string;
  code?: string;
  tier?: "standard" | "vip" | "ultra_vip";
  discount?: number;
  discount_percent?: number;
  remaining_uses?: number;
  uses_remaining?: number;
  max_uses?: number;
  expires_at?: string;
  valid_until_iso?: string;
  valid_for_seconds?: number;
  checkout_plan_tier?: string;
  checkout_product_id?: string;
  checkout_amount_cents?: number;
  checkout_currency?: string;
  checkout_product_name?: string;
  checkout_payment_provider?: string;
  checkout_discount_percentage?: number;
  layout?: {
    title?: string;
    subtitle?: string;
    message?: string;
    theme?: string;
    cta_text?: string;
    cta_url?: string;
    ctaUrl?: string;
    image_url?: string;
    imageUrl?: string;
    background_image_url?: string;
    background_url?: string;
    backgroundImageUrl?: string;
    backgroundUrl?: string;
    spots_remaining?: number;
    max_spots?: number;
    /** Mux playback ID asignado por link (admin). */
    mux_playback_id?: string;
    playback_id?: string;
    cta_subtitle?: string;
  };
}

type CacheEntry = {
  data: InvitationValidateApiResponse;
  expiresAt: number;
};

const VALIDATION_CACHE_TTL_MS = 30_000;
const validationCache = new Map<string, CacheEntry>();
const validationInFlight = new Map<string, Promise<InvitationValidateApiResponse>>();

function buildValidationKey(value: string, mode: InvitationValidationMode): string {
  return `${mode}:${value}`;
}

async function fetchInvitationValidation(
  value: string,
  mode: InvitationValidationMode,
): Promise<InvitationValidateApiResponse> {
  // getApiBaseUrl() devuelve el mismo origen en producción (API same-origin):
  // eso ES una API configurada. isApiUrlConfigured() solo detecta el override
  // VITE_API_URL de dev — usarlo acá bloqueaba la validación real en prod
  // ("API no configurada") y solo funcionaban los códigos de fallback.
  const base = getApiBaseUrl();
  if (!base) {
    return {
      valid: false,
      message: "No se pudo verificar la invitacion. API no configurada.",
    };
  }
  let url = `${base}/api/invitation-codes/validate?${mode}=${encodeURIComponent(value)}`;
  let response = await fetch(url);

  // Fallback legacy cuando /validate no existe.
  if (response.status === 404 && mode === "code") {
    url = `${base}/api/invitation-codes?code=${encodeURIComponent(value)}`;
    response = await fetch(url);
  }

  const contentType = response.headers.get("content-type") || "";
  if (!contentType.includes("application/json")) {
    return {
      valid: false,
      message: "No se pudo verificar la invitacion. Intenta de nuevo.",
    };
  }

  const raw = await response.json();
  const data =
    raw?.data && typeof raw.data === "object" ? (raw.data as InvitationValidateApiResponse) : (raw as InvitationValidateApiResponse);

  return data;
}

export async function validateInvitationValue(
  value: string,
  mode: InvitationValidationMode,
): Promise<InvitationValidateApiResponse> {
  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return { valid: false, message: "Invitacion no valida." };
  }

  const key = buildValidationKey(normalizedValue, mode);
  const now = Date.now();
  const cached = validationCache.get(key);
  if (cached && cached.expiresAt > now) {
    return cached.data;
  }

  const inFlight = validationInFlight.get(key);
  if (inFlight) {
    return inFlight;
  }

  const requestPromise = fetchInvitationValidation(normalizedValue, mode)
    .then((data) => {
      // No cachear valid:true — cada apertura debe ir al servidor (1 uso / ventana desde 1er open).
      if (!data.valid) {
        validationCache.set(key, {
          data,
          expiresAt: Date.now() + VALIDATION_CACHE_TTL_MS,
        });
      }
      return data;
    })
    .catch(() => {
      return {
        valid: false,
        message: "No se pudo verificar la invitacion. Verifica tu conexion e intenta de nuevo.",
      };
    })
    .finally(() => {
      validationInFlight.delete(key);
    });

  validationInFlight.set(key, requestPromise);
  return requestPromise;
}

// Compatibilidad con implementaciones existentes.
export function validateInvitationCode(code: string): Promise<InvitationValidateApiResponse> {
  return validateInvitationValue(code, "code");
}

export function validateInvitationRef(ref: string): Promise<InvitationValidateApiResponse> {
  return validateInvitationValue(ref, "ref");
}
